import React, {useEffect, useRef} from 'react'
import type {GeoJsonObject} from 'geojson'

import L from './leaflet.js'
import {addScaleControl, baseMapOptions, createTileLayer, defaultCenter, defaultZoom, fitBoundsSafe} from './setup.js'
import {setupDrawing, toZoneGeoJSON, zoneStyle} from './geoman.js'
import {sameZoneLayers, sameZoneShapes, ZoneLayer} from './zoneLayers.js'
import {samePosition, validPosition} from './geometry.js'
import useLeafletMap from './hooks/useLeafletMap.js'
import useLayerGroup from './hooks/useLayerGroup.js'
import useLatest from './hooks/useLatest.js'
import MapControls from './controls/MapControls.js'
import ZoneDrawingControls from './controls/ZoneDrawingControls.js'
import MapAttribution from './controls/MapAttribution.js'
import {mapContainerStyle, mapElementStyle} from './styles.js'

export interface AreaMapProps {
    /** Shown when no layer has an area (a delivery area of only postal codes) */
    center?: {lat?: number, lng?: number}
    /** The zones to draw, a new array every render is fine */
    layers?: ZoneLayer[]
    /** With it the map gets buttons to draw a zone (polygon or circle), called with the drawn zone's GeoJSON */
    onCreate?: (geoJSON: object) => void
}

// Zoom on the center when there are no areas to fit
const centerZoom = 12

// A feature group for getBounds of all zones
const createFeatureGroup = () => L.featureGroup()

/**
 * A map with delivery zones (of a partner, or the ones shared with a partner), optionally with the tools to draw a
 * new one. It fits the zones when the areas change, not on every render, so the user's view stays.
 */
export default function AreaMap({center, layers = [], onCreate}: AreaMapProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const latest = useLatest({onCreate})

    const map = useLeafletMap(containerRef, baseMapOptions, (map) => {
        map.setView(validPosition(center) || defaultCenter, defaultZoom)

        createTileLayer().addTo(map)
        addScaleControl(map)
        setupDrawing(map)

        // The drawn shape isn't kept: the caller adds the zone, which comes back in layers
        map.on('pm:create', ({shape, layer}) => {
            layer.remove()
            latest.current.onCreate?.(toZoneGeoJSON(shape, layer))
        })
    })

    const group = useLayerGroup(map, createFeatureGroup)
    const shown = useRef<{layers: ZoneLayer[], center: AreaMapProps['center']} | null>(null)

    useEffect(() => {
        if (!map || !group) {
            return
        }

        const previous = shown.current
        shown.current = {layers, center}

        if (!previous || !sameZoneLayers(previous.layers, layers)) {
            group.clearLayers()

            for (const layer of layers) {
                if (!layer.geoJSON) {
                    continue
                }

                try {
                    L.geoJSON(layer.geoJSON as GeoJsonObject, {
                        style: () => zoneStyle(layer.color),
                        onEachFeature: (feature, featureLayer) => group.addLayer(featureLayer)
                    })
                } catch (error) {
                    // An invalid zone is left out instead of breaking the map
                    console.error(error)
                }
            }
        }

        if (layers.some((layer) => layer.geoJSON)) {
            if (!previous || !sameZoneShapes(previous.layers, layers)) {
                fitBoundsSafe(map, group.getBounds())
            }
        } else if (!previous || !samePosition(previous.center, center)) {
            const position = validPosition(center)

            if (position) {
                map.setView(position, centerZoom)
            }
        }
    }, [map, group, layers, center])

    return (
        <div style={mapContainerStyle}>
            <div ref={containerRef} style={mapElementStyle} />
            <MapControls map={map}>
                {/* Without onCreate the card goes, which stops drawing */}
                {!!onCreate && <ZoneDrawingControls map={map} tools={['Polygon', 'Circle']} />}
            </MapControls>
            <MapAttribution withScale />
        </div>
    )
}
